import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Carousel,
  Spinner,
  Table,
  Button,
} from 'react-bootstrap';
import shortDateFormat from '../../../../../helpers/dates';
import BasePage from '../../../BasePage/BasePage';
import LiveInput from '../../../../atoms/LiveInput/LiveInput';
import Icon from '../../../../atoms/Icon/Icon';
import Sidebar from '../../../../molecules/Sidebar/Sidebar';
import CitationList from '../../../../Organisms/CitationList/CitationList';
import DeleteModal from '../../../../molecules/DeleteModal/DeleteModal';
import DocumentsList from '../../../../Organisms/DocumentsList/DocumentsList';
import {
  fetchCharacteristicsInitAction,
  updateSelectedCharacteristicAction,
} from '../../../../../actions/instances/characteristics';
import { setSelectedBuildingAction } from '../../../../../actions/instances/buildings';
import Api from '../../../../../services/Api';
import { publishCharacteristic } from '../../../../../helpers/publish';
import InstanceMetadata from '../../../../Organisms/InstanceMetadata/InstanceMetadata';
import './EditViewCharacteristic.scss';
import { loadCharacteristicMetadata } from '../../../../../helpers/metadata';

const EditViewCharacteristic = () => {
  const {
    selectedCharacteristic,
  } = useSelector(state => state.characteristics);
  const { user } = useSelector(state => state.auth);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteModalShown, setIsDeleteModalShown] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [buildings, setBuildings] = useState([]);
  const dispatch = useDispatch();
  const history = useHistory();
  if (!selectedCharacteristic) {
    history.push('/instances/building-characteristics');
  }
  const loadMetadata = async () => {
    const metadata = await loadCharacteristicMetadata(selectedCharacteristic.id);
    setBuildings(metadata.buildings);
  };
  useEffect(() => {
    loadMetadata().then(() => console.log('loaded'));
  }, []);
  const deleteCharacteristic = () => {
    setIsDeleting(true);
    Api.delete(`/instances/building-characteristics/${selectedCharacteristic.id}`).then((response) => {
      if (response.status === 204) {
        dispatch(fetchCharacteristicsInitAction());
        setTimeout(() => {
          history.goBack();
          setIsDeleting(false);
        }, 500);
      } else {
        setIsDeleting(false);
      }
    });
  };
  return (
    <BasePage>
      <DeleteModal
        onDelete={deleteCharacteristic}
        isShown={isDeleteModalShown}
        isLoading={isDeleting}
        onClose={() => setIsDeleteModalShown(false)}
        isAllowed
      >
        Are you sure you want to delete
        {' '}
        <strong>{selectedCharacteristic.name}</strong>
        {'? '}
        <br />
        This action cannot be undone.
      </DeleteModal>
      <div className="col-md-3">
        <div className="row">
          <div className="col-md-12">
            {selectedCharacteristic.images && selectedCharacteristic.images.length > 0 ? (
              <Carousel>
                {selectedCharacteristic.images.map(image => (
                  <Carousel.Item>
                    <img
                      className="d-block w-100 img-fluid"
                      src={image.url}
                      alt={image.label}
                      style={{
                        maxHeight: '400px',
                        objectFit: 'cover',
                      }}
                    />
                  </Carousel.Item>
                ))}
              </Carousel>
            ) : (
              <img src="/images/default.jpg" alt={selectedCharacteristic.name} className="img-fluid" />
            )}
          </div>
          <div className="col-md-12 mt-2 d-grid gap-2">
            {user.role_id !== 3 && (
              <Button
                variant={selectedCharacteristic.in_review === 1 ? 'success' : 'warning'}
                onClick={async () => {
                  setIsPublishing(true);
                  const characteristic = await publishCharacteristic(
                    selectedCharacteristic.id,
                    selectedCharacteristic.in_review,
                  );
                  dispatch(updateSelectedCharacteristicAction(characteristic));
                  dispatch(fetchCharacteristicsInitAction());
                  setIsPublishing(false);
                }}
              >
                {isPublishing ? (
                  <>
                    <Spinner
                      as="span"
                      animation="grow"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                    <span className="ms-2">Loading...</span>
                  </>
                ) : (
                  <>
                    <Icon icon="check" />
                    <span className={`ms-2 ${selectedCharacteristic.in_review === 0 ? 'text-white' : ''}`}>
                      {selectedCharacteristic.in_review === 1 ? 'Approve' : 'Disapprove'}
                    </span>
                  </>
                )}
              </Button>
            )}
            <Button
              variant="dark"
              onClick={() => {
                dispatch(updateSelectedCharacteristicAction(selectedCharacteristic));
                history.push('/instances/building-characteristics/edit');
              }}
            >
              <Icon icon="edit" />
              <span className="ms-2">Edit</span>
            </Button>
            <Button variant="dark" onClick={() => history.goBack()}>
              <Icon icon="times" />
              <span className="ms-2">Cancel</span>
            </Button>
            {user.role_id !== 3 && (
              <Button variant="danger" onClick={() => setIsDeleteModalShown(true)}>
                {isDeleting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="grow"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                    <span className="ms-2">Loading...</span>
                  </>
                ) : (
                  <>
                    <Icon icon="trash" />
                    <span className="ms-2">Delete</span>
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
      <div className="col-md-5">
        <div className="row d-flex align-items-center mb-3">
          <div className="col-md-1 text-end">
            <Icon icon="edit" color="#000" />
          </div>
          <div className="col-md-11 text-start">
            <LiveInput endpoint="/instances/building-components" fieldName="name" id={selectedCharacteristic.id} value={selectedCharacteristic.name} fontSize={22} />
          </div>
        </div>
        <div className="row border-top border-bottom mt-2 p-2">
          <div className="col-md-12">
            <div className="border-end d-inline pe-2 me-2">
              <span className="me-1">
                <Icon icon="calendar" color="#000" />
              </span>
              {shortDateFormat(selectedCharacteristic.created_at)}
            </div>
            <div className="d-inline">
              {selectedCharacteristic.tags.map(tag => (
                <span className="me-2 border-end pe-2">
                  <Icon icon="tag" color="#000" />
                  <span className="ms-2">
                    {tag.name}
                  </span>
                </span>
              ))}
            </div>
            <div className="d-inline border-end d-inline pe-2 me-2">
              <Icon icon="map" color="#000" />
              <span className="ms-2">
                {`${selectedCharacteristic.city.name}, ${selectedCharacteristic.city.country.name} [${selectedCharacteristic.city.code}-${selectedCharacteristic.city.country.code}]`}
              </span>
            </div>
            <div className="d-inline">
              <Icon icon="globe" color="#000" />
              <span className="ms-2">
                {`${selectedCharacteristic.dynasty.name} [${selectedCharacteristic.dynasty.code}]`}
              </span>
            </div>
          </div>
        </div>

        <InstanceMetadata instance={selectedCharacteristic} title="Characteristic Metadata" />

        <div className="row mb-5 mt-3">
          <h4>Buildings</h4>
          <br />
          <Table responsive striped hover>
            <thead>
              <tr>
                <td />
                <td>Building</td>
                <td>Dynasty</td>
              </tr>
            </thead>
            <tbody>
              {buildings && buildings.length > 0 ? (
                buildings.map(building => (
                  <tr>
                    <td>
                      {building.images.length > 0 ? (
                        <img alt={building.name} src={building.images[0].url} className="img-fluid" height={50} width={50} />
                      ) : (
                        <img alt={building.name} src="/images/default.jpg" className="img-fluid" height={50} width={50} />
                      )}
                    </td>
                    {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                    <td
                      onClick={() => {
                        dispatch(setSelectedBuildingAction(building));
                        history.push('/buildings/view');
                        window.scrollTo(0, 0);
                      }}
                    >
                      {`${building.name} #${building.number}`}
                    </td>
                    <td>{`${building.dynasty.name} [${building.dynasty.code}]`}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="text-center">No buildings with this characteristic were found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
        <DocumentsList documents={selectedCharacteristic ? selectedCharacteristic.documents : []} title="Characteristic Documents" />
        <CitationList citations={selectedCharacteristic ? selectedCharacteristic.citation : []} title="Characteristic Citations" />
      </div>
      <div className="col-md-3 mt-5">
        <Sidebar />
      </div>
    </BasePage>
  );
};

export default EditViewCharacteristic;
